import { withNamespaces } from "react-i18next";
import Accordion from 'react-bootstrap/Accordion';
import { Col, Row, Table, UncontrolledTooltip } from "reactstrap";
import { RiExternalLinkLine, RiFileList3Line, RiInformationLine } from "react-icons/ri";

const FormatPrice = ({
    value, 
    currency
}) => {
    if (value === undefined || value === null) {
        return '—'
    }

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        maximumFractionDigits: 2
    }).format(value)
}

const FinancialSummary = ({
    t,
    setActiveAccordion,
    activeAccordion,
    detailsQuery
}) => {
    const organizationData = JSON.parse(detailsQuery.data?.company?.organizationData || "");

    const {
        quickcheck,
        paydex_score_earlier,
        high_credit,
        average_high_credit,
        total_payments,
        credit_score,
        global_failure_score
    } = organizationData || {};

    return (
        <Accordion.Item 
            onClick={() => setActiveAccordion('payment-behavior')}
            className={activeAccordion === 'payment-behavior' ? 'active' : ''}
            eventKey={'payment-behavior'}
        >
            <Accordion.Header>
                <RiFileList3Line size="24" className="me-2" />
                <span>
                    {t('Payment & Credit')}
                </span>
            </Accordion.Header>

            <Accordion.Body>
                <Row>
                    <Col lg="6">
                        <Table striped bordered hover size="sm">
                            <tbody>
                                <tr>
                                    <th
                                        style={{
                                            width: '40%'
                                        }}
                                    >
                                        {t('PAYDEX Score')}
                                        <span
                                            id="tooltip-payment_paydex_score"
                                            className="ms-1"
                                            style={{ cursor: "pointer" }}>
                                            <RiInformationLine />
                                        </span>

                                        <a
                                            href="https://www.dnb.com/en-us/smb/resources/credit-scores/what-is-paydex-score.html"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-decoration-none text-link"
                                        >
                                            <RiExternalLinkLine size={16} className="ms-1" />
                                        </a>

                                        <UncontrolledTooltip
                                            target="tooltip-payment_paydex_score"
                                        >
                                            {t('The PAYDEX Score is a dollar-weighted indicator intended to reflect a business’s past payment performance. Companies receive a score between 1 and 100, where a higher number represents a greater likelihood that a business will pay its debts on time.')}
                                        </UncontrolledTooltip>
                                    </th>
                                    <td className="text-end">
                                        {quickcheck.paydex_score ?? '—'}
                                    </td>
                                </tr>
                                <tr>
                                    <th>
                                        {t('Previous PAYDEX')}
                                        <span
                                            id="tooltip-payment_paydex_score_earlier"
                                            className="ms-1"
                                            style={{ cursor: "pointer" }}>
                                            <RiInformationLine />
                                        </span>
                                        <UncontrolledTooltip
                                            target="tooltip-payment_paydex_score_earlier"
                                        >
                                            {t('D&B Paydex Score from 3 months ago.')}
                                        </UncontrolledTooltip>
                                    </th>
                                    <td className="text-end">
                                        {paydex_score_earlier ?? '—'}
                                    </td>
                                </tr>
                                <tr>
                                    <th>
                                        {t('High Credit')}
                                        <span
                                            id="tooltip-payment_high_credit"
                                            className="ms-1"
                                            style={{ cursor: "pointer" }}>
                                            <RiInformationLine />
                                        </span>
                                        <UncontrolledTooltip
                                            target="tooltip-payment_high_credit"
                                        >
                                            {t('Highest balance of the account during the past 12 months.')}
                                        </UncontrolledTooltip>
                                    </th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            value={high_credit}
                                            currency={quickcheck.currency}
                                            t={t}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>
                                        {t('Average High Credit')}
                                        <span
                                            id="tooltip-payment_average_high_credit"
                                            className="ms-1"
                                            style={{ cursor: "pointer" }}>
                                            <RiInformationLine />
                                        </span>
                                        <UncontrolledTooltip
                                            target="tooltip-payment_average_high_credit"
                                        >
                                            {t('The average high credit represents the median of the highest credit extended to the subject business over the past 12 months. It is calculated by taking the sum of all high credit dollar amounts (unique vendor account experiences) divided by the total number of account experiences.')}
                                        </UncontrolledTooltip>
                                    </th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            value={average_high_credit}
                                            currency={quickcheck.currency}
                                            t={t}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>

                    <Col lg="6">
                        <Table striped bordered hover size="sm">
                            <tbody>
                                <tr>
                                    <th>
                                        {t('Total Payments Recorded')}
                                    </th>
                                    <td className="text-end">
                                        {total_payments}
                                    </td>
                                </tr>
                                <tr>
                                    <th>
                                        {t('Maximum Credit')}
                                        <span
                                            id="tooltip-payment_maximum_credit"
                                            className="ms-1"
                                            style={{ cursor: "pointer" }}>
                                            <RiInformationLine />
                                        </span>
                                        <UncontrolledTooltip
                                            target="tooltip-payment_maximum_credit"
                                        >
                                            {t('Maximum amount of credit recommended.  For Europe only.')}
                                        </UncontrolledTooltip>
                                    </th>
                                    <td className="text-end">
                                        <FormatPrice 
                                            value={quickcheck.maximum_credit}
                                            currency={quickcheck.currency}
                                            t={t}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>
                                        {t('Credit Score')}
                                    </th>
                                    <td>
                                        {credit_score.length ? JSON.stringify(credit_score) : "No Data"}
                                    </td>
                                </tr>
                                <tr>
                                    <th>
                                        {t('Global Failure Score')}
                                    </th>
                                    <td>
                                        {global_failure_score.length ? JSON.stringify(global_failure_score) : "No Data"}
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default withNamespaces()(
    FinancialSummary
);